import { useState } from "react";
import { Link } from "wouter";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useCreateFeatureRequest,
  useGetFeatureCatalogue,
  getGetFeatureCatalogueQueryKey,
  getListFeatureRequestsQueryKey,
  ApiError,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

/**
 * Shown when a tenant navigates to a route gated by a feature flag that is
 * currently disabled for their account. Provides a one-click "Request access"
 * CTA that flows through the existing tenant_feature_requests mechanism so
 * platform admins see the pending request in the Service Desk / Feature
 * Requests view.
 */
export function FeatureLockedPage({
  featureKey,
  label,
}: {
  featureKey: string;
  label: string;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: catalogue } = useGetFeatureCatalogue({
    query: { queryKey: getGetFeatureCatalogueQueryKey() },
  });
  const create = useCreateFeatureRequest();
  const [localRequested, setLocalRequested] = useState(false);

  const catalogueItem = catalogue?.items.find((i) => i.key === featureKey);
  const openRequest = catalogueItem?.openRequest ?? null;
  const canRequest = catalogue?.canRequestFeature ?? false;

  function handleRequest() {
    create.mutate(
      {
        data: {
          requestType: "enable_module",
          featureKey,
          priority: "normal",
          title: `Enable ${label}`,
          businessReason: null,
          details: `Requested access to the ${label} module from the locked feature page.`,
        },
      },
      {
        onSuccess: () => {
          setLocalRequested(true);
          toast({
            title: "Request sent",
            description: `We'll review your ${label} request soon.`,
          });
          void qc.invalidateQueries({
            queryKey: getGetFeatureCatalogueQueryKey(),
          });
          void qc.invalidateQueries({
            queryKey: getListFeatureRequestsQueryKey(),
          });
        },
        onError: (e: unknown) => {
          // 409 means an open request already exists — silently refresh the
          // catalogue so the "View request" link appears instead of toasting.
          if (e instanceof ApiError && e.status === 409) {
            setLocalRequested(true);
            void qc.invalidateQueries({
              queryKey: getGetFeatureCatalogueQueryKey(),
            });
            return;
          }
          toast({
            title: "Could not send request",
            description: e instanceof Error ? e.message : String(e),
            variant: "destructive",
          });
        },
      },
    );
  }

  const hasOpenRequest = openRequest !== null || localRequested;

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-4 p-12 text-center"
      data-testid="feature-locked"
    >
      <div className="rounded-full bg-amber-50 p-4 text-amber-600 dark:bg-amber-950">
        <Lock className="h-10 w-10" />
      </div>
      <h1 className="text-2xl font-semibold">{label} is not enabled</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        This module is not active on your account. Request access and our team
        will enable it for you.
      </p>

      {hasOpenRequest ? (
        openRequest ? (
          <div className="flex flex-col items-center gap-2">
            <p className="text-sm text-muted-foreground">
              Your request is being reviewed.
            </p>
            <Button asChild variant="outline">
              <Link href={`/account-portal/requests/${openRequest.id}`}>
                View request
              </Link>
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Your request has been submitted. We&apos;ll be in touch soon.
          </p>
        )
      ) : canRequest ? (
        <Button onClick={handleRequest} disabled={create.isPending}>
          {create.isPending ? "Requesting…" : "Request access"}
        </Button>
      ) : (
        <p className="text-sm text-muted-foreground">
          Ask an account admin to request this module from{" "}
          <Link href="/account-portal/features" className="underline">
            Available Features
          </Link>
          .
        </p>
      )}
    </div>
  );
}
